import {
    Add,
    ChevronRight,
    Extension,
} from '@mui/icons-material';
import {
    Avatar,
    Box,
    Button,
    Card,
    CardActionArea,
    Chip,
    MenuItem,
    Stack,
    TextField,
    Typography,
} from '@mui/material';
import { useMemo, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { EmptyContentComponent } from '../../../components/Content';
import { LoadingContent } from '../../../components/Loading';
import ListPageLayout from '../../../components/ListPageLayout';
import { useFetch } from '../../../hooks/useFecth';
import { useSearchTextInput } from '../../../hooks/useSearchTextInput';
import DialogPlans from './components/DialogAditional';

const statusOptions = [
    { value: '', label: 'Todos' },
    { value: 'true', label: 'Ativos' },
    { value: 'false', label: 'Inativos' },
];

const productTypeOptions = [
    { value: '', label: 'Todos os tipos' },
    { value: 'additional', label: 'Adicional' },
    { value: 'custom', label: 'Customização' },
];

const additionalTypeLabels = {
    telemedicina: 'Telemedicina',
    telepsicologia: 'Telepsicologia',
    seguro: 'Seguro de vida',
    telepet: 'Telepet',
    custom_app: 'Aplicativo',
    custom_telemedicina: 'Telemedicina',
    custom_telepet: 'Telepet',
};

function formatCurrency(value) {
    return Number(value ?? 0).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });
}

function getAdditionalTypeLabel(value) {
    if (!value) return null;
    return additionalTypeLabels[value] || value;
}

function getProductTypeLabel(type) {
    if (type === 'custom') return 'Customização';
    if (type === 'additional') return 'Adicional';
    return type || 'Produto';
}

function AdditionalItem({ item, viewMode }) {
    if (viewMode === 'cards') {
        return (
            <Card variant="outlined" sx={{ height: '100%' }}>
                <CardActionArea component={Link} to={`#additional|${item.id}`} sx={{ p: 2, height: '100%' }}>
                    <Stack spacing={1.25}>
                        <Avatar
                            src={item.image}
                            variant="rounded"
                            sx={{
                                bgcolor: item.type === 'custom' ? 'secondary.main' : 'primary.main',
                            }}
                        >
                            <Extension />
                        </Avatar>
                        <Typography fontWeight={700}>{item.name}</Typography>
                        <Stack direction="row" gap={0.5} flexWrap="wrap">
                            <Chip
                                size="small"
                                color={item.status ? 'success' : 'default'}
                                label={item.status ? 'Ativo' : 'Inativo'}
                            />
                            <Chip size="small" variant="outlined" label={getProductTypeLabel(item.type)} />
                        </Stack>
                        <Typography variant="body2" fontWeight={600}>
                            {formatCurrency(item.price)}
                        </Typography>
                    </Stack>
                </CardActionArea>
            </Card>
        );
    }

    return (
        <Card variant="outlined">
            <CardActionArea component={Link} to={`#additional|${item.id}`} sx={{ p: { xs: 2, sm: 2.5 } }}>
                <Stack
                    direction={{ xs: 'column', sm: 'row' }}
                    spacing={{ xs: 2, sm: 3 }}
                    alignItems={{ xs: 'flex-start', sm: 'center' }}
                >
                    <Avatar
                        src={item.image}
                        variant="rounded"
                        sx={{
                            width: { xs: 48, sm: 56 },
                            height: { xs: 48, sm: 56 },
                            bgcolor: item.type === 'custom' ? 'secondary.main' : 'primary.main',
                            border: '1px solid',
                            borderColor: 'divider',
                        }}
                    >
                        <Extension />
                    </Avatar>

                    <Stack spacing={1} sx={{ flex: 1, minWidth: 0 }}>
                        <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
                            <Typography
                                variant="h6"
                                sx={{ fontSize: { xs: '1rem', sm: '1.1rem' }, fontWeight: 600 }}
                            >
                                {item.name}
                            </Typography>
                            <Chip label={`#${item.id}`} size="small" sx={{ fontWeight: 600 }} />
                            <Chip
                                size="small"
                                color={item.status ? 'success' : 'default'}
                                variant={item.status ? 'filled' : 'outlined'}
                                label={item.status ? 'Ativo' : 'Inativo'}
                            />
                            <Chip size="small" variant="outlined" label={getProductTypeLabel(item.type)} />
                        </Stack>

                        {item.description ? (
                            <Typography
                                variant="body2"
                                color="text.secondary"
                                sx={{
                                    display: '-webkit-box',
                                    WebkitLineClamp: 2,
                                    WebkitBoxOrient: 'vertical',
                                    overflow: 'hidden',
                                }}
                            >
                                {item.description}
                            </Typography>
                        ) : null}

                        <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                            <Chip size="small" variant="outlined" label={`Preço: ${formatCurrency(item.price)}`} />
                            {Boolean(item.priceSuggestion) && (
                                <Chip
                                    size="small"
                                    variant="outlined"
                                    label={`Sugerido: ${formatCurrency(item.priceSuggestion)}`}
                                />
                            )}
                            {getAdditionalTypeLabel(item.additionalType) && (
                                <Chip
                                    size="small"
                                    variant="outlined"
                                    color="info"
                                    label={getAdditionalTypeLabel(item.additionalType)}
                                />
                            )}
                            {Boolean(item.numberDependents) && (
                                <Chip
                                    size="small"
                                    variant="outlined"
                                    label={`${item.numberDependents} dependentes`}
                                />
                            )}
                        </Stack>
                    </Stack>

                    <ChevronRight color="action" sx={{ display: { xs: 'none', sm: 'block' } }} />
                </Stack>
            </CardActionArea>
        </Card>
    );
}

export default function Aditionals() {
    const [searchParams, setSearchParams] = useSearchParams();
    const page = parseInt(searchParams.get('page'), 10) || 1;
    const pageSize = parseInt(searchParams.get('per_page'), 10) || 10;
    const { search, input, handleChange, handleReset } = useSearchTextInput(searchParams.get('search') || '');
    const [statusFilter, setStatusFilter] = useState(searchParams.get('status') || '');
    const [typeFilter, setTypeFilter] = useState(searchParams.get('type') || '');

    const fetchParams = useMemo(
        () => ({
            paged: true,
            page,
            per_page: pageSize,
            search: search.trim() || undefined,
            status: statusFilter || undefined,
            type: typeFilter || undefined,
        }),
        [page, pageSize, search, statusFilter, typeFilter],
    );

    const { data, mutate, isFetching } = useFetch('additional', { params: fetchParams });
    const rows = data?.rows ?? [];
    const hasFilters = Boolean(search.trim() || statusFilter || typeFilter);

    const applyFilters = close => {
        const nextParams = new URLSearchParams();
        if (search.trim()) nextParams.set('search', search.trim());
        if (statusFilter) nextParams.set('status', statusFilter);
        if (typeFilter) nextParams.set('type', typeFilter);
        if (pageSize !== 10) nextParams.set('per_page', String(pageSize));
        nextParams.set('page', '1');
        setSearchParams(nextParams);
        close?.();
    };

    const handleClearFilters = () => {
        handleReset();
        setStatusFilter('');
        setTypeFilter('');
        setSearchParams({});
    };

    const handlePageChange = (_, newPage) => {
        const nextParams = new URLSearchParams(searchParams);
        nextParams.set('page', String(newPage));
        setSearchParams(nextParams);
    };

    const handlePageSizeChange = nextSize => {
        const nextParams = new URLSearchParams(searchParams);
        nextParams.set('per_page', String(nextSize));
        nextParams.set('page', '1');
        setSearchParams(nextParams);
    };

    return (
        <>
            <ListPageLayout
                title="Adicionais"
                subtitle="Produtos adicionais e customizações disponíveis para os clubes."
                counts={[{ label: 'produtos', value: data?.numRows ?? 0, color: 'primary' }]}
                loading={isFetching && !data}
                filterActive={hasFilters}
                onClearFilters={hasFilters ? handleClearFilters : null}
                viewStorageKey="admin-aditionals-view"
                actions={
                    <Button size="small" variant="contained" startIcon={<Add />} component={Link} to="#additional">
                        Adicionar produto
                    </Button>
                }
                isEmpty={!isFetching && rows.length === 0}
                empty={
                    <Stack spacing={2} alignItems="center">
                        <EmptyContentComponent />
                        <Typography color="text.secondary">
                            {hasFilters
                                ? 'Nenhum produto encontrado com os filtros aplicados.'
                                : 'Nenhum produto cadastrado ainda.'}
                        </Typography>
                        {hasFilters ? (
                            <Button variant="outlined" onClick={handleClearFilters}>
                                Limpar filtros
                            </Button>
                        ) : (
                            <Button variant="contained" startIcon={<Add />} component={Link} to="#additional">
                                Cadastrar primeiro produto
                            </Button>
                        )}
                    </Stack>
                }
                pagination={{
                    page,
                    pages: data?.pages || 1,
                    total: data?.numRows ?? 0,
                    pageSize,
                    onChange: handlePageChange,
                    onPageSizeChange: handlePageSizeChange,
                    itemLabel: (data?.numRows ?? 0) === 1 ? 'produto' : 'produtos',
                }}
                filterPanel={({ close }) => (
                    <Stack
                        spacing={2}
                        component="form"
                        onSubmit={e => {
                            e.preventDefault();
                            applyFilters(close);
                        }}
                    >
                        <TextField
                            fullWidth
                            size="small"
                            label="Buscar produto"
                            placeholder="Nome ou descrição"
                            value={input}
                            onChange={event => handleChange(event.target.value)}
                        />
                        <TextField
                            select
                            fullWidth
                            size="small"
                            label="Status"
                            value={statusFilter}
                            onChange={event => setStatusFilter(event.target.value)}
                        >
                            {statusOptions.map(option => (
                                <MenuItem key={option.value || 'all-status'} value={option.value}>
                                    {option.label}
                                </MenuItem>
                            ))}
                        </TextField>
                        <TextField
                            select
                            fullWidth
                            size="small"
                            label="Categoria"
                            value={typeFilter}
                            onChange={event => setTypeFilter(event.target.value)}
                        >
                            {productTypeOptions.map(option => (
                                <MenuItem key={option.value || 'all-type'} value={option.value}>
                                    {option.label}
                                </MenuItem>
                            ))}
                        </TextField>
                        <Button type="submit" variant="contained" fullWidth>
                            Aplicar filtros
                        </Button>
                    </Stack>
                )}
            >
                {({ viewMode }) =>
                    isFetching && !data ? (
                        <LoadingContent />
                    ) : (
                        <Box
                            sx={{
                                p: 2,
                                display: viewMode === 'cards' ? 'grid' : 'flex',
                                flexDirection: 'column',
                                gap: 1.5,
                                gridTemplateColumns:
                                    viewMode === 'cards'
                                        ? { xs: '1fr', sm: '1fr 1fr', md: '1fr 1fr 1fr' }
                                        : undefined,
                            }}
                        >
                            {rows.map(item => (
                                <AdditionalItem key={item.id} item={item} viewMode={viewMode} />
                            ))}
                        </Box>
                    )
                }
            </ListPageLayout>
            <DialogPlans mutate={mutate} />
        </>
    );
}
